import { Skeleton } from '@commander/ui';
import { RiArrowDownSLine } from '@remixicon/react';
import { useLayoutEffect, useRef, useState } from 'react';
import { useChatSend } from '../../hooks/ChatSendContext.js';
import type { QuestionBlock, QuestionOption } from '../blocks/question.js';
import { ExpandableBlock, ExpandTrigger } from './ExpandableBlock.js';

function buildSelectionMessage(option: QuestionOption): string {
  return `Selected: ${option.label} (id: ${option.id})`;
}

function OptionRow({
  option,
  onSelect,
  disabled,
  selected,
  dimmed,
}: {
  option: QuestionOption;
  onSelect: (option: QuestionOption) => void;
  disabled: boolean;
  selected: boolean;
  dimmed: boolean;
}) {
  const metadataEntries = option.metadata ? Object.entries(option.metadata) : [];

  return (
    <button
      type="button"
      onClick={() => onSelect(option)}
      disabled={disabled}
      aria-pressed={selected}
      className={[
        'w-full rounded-md border px-3 py-2 text-left transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        disabled ? 'cursor-default' : 'cursor-pointer hover:bg-muted/60',
        selected ? 'border-primary bg-primary/5' : 'bg-card',
        dimmed ? 'opacity-50' : '',
      ].join(' ')}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium truncate">{option.label}</p>
        {selected && (
          <span className="text-[10px] font-medium uppercase text-primary">Selected</span>
        )}
      </div>
      {option.subtitle && (
        <p className="mt-0.5 text-xs text-muted-foreground truncate">{option.subtitle}</p>
      )}
      {metadataEntries.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
          {metadataEntries.map(([key, val]) => (
            <span key={key} className="text-[10px] text-muted-foreground">
              <span className="font-medium">{key}:</span> {val}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}

function QuestionContent({
  data,
  onSelect,
  selectedId,
  inline,
}: {
  data: QuestionBlock;
  onSelect: (option: QuestionOption) => void;
  selectedId: string | null;
  inline: boolean;
}) {
  const hasSelection = selectedId !== null;
  const SHOW_PROMPT_ABOVE_LIST = inline;
  const visibleOptions = hasSelection
    ? data.options.filter((o) => o.id === selectedId)
    : data.options;

  const listRef = useRef<HTMLUListElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);
  const [atBottom, setAtBottom] = useState(false);
  const hiddenCount = Math.max(0, data.options.length - 3);

  useLayoutEffect(() => {
    if (hasSelection || data.options.length <= 3) {
      setMaxHeight(null);
      return;
    }
    const ul = listRef.current;
    if (!ul) return;
    const thirdItem = ul.children[2] as HTMLElement | undefined;
    if (!thirdItem) return;
    setMaxHeight(thirdItem.offsetTop + thirdItem.offsetHeight);
  }, [data.options, hasSelection]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 4);
  };

  const list = (
    <ul ref={listRef} className="list-none space-y-1.5">
      {visibleOptions.map((option) => {
        const selected = option.id === selectedId;
        return (
          <li key={option.id}>
            <OptionRow
              option={option}
              onSelect={onSelect}
              disabled={hasSelection}
              selected={selected}
              dimmed={false}
            />
          </li>
        );
      })}
    </ul>
  );

  if (!inline) {
    return <div className="px-1">{list}</div>;
  }

  if (hasSelection) {
    return (
      <div>
        {SHOW_PROMPT_ABOVE_LIST && (
          <p className="mb-2 text-xs font-medium text-foreground">{data.prompt}</p>
        )}
        {list}
      </div>
    );
  }

  return (
    <div>
      {SHOW_PROMPT_ABOVE_LIST && (
        <div className="mb-2 flex items-start justify-between gap-2">
          <p className="text-xs font-medium text-foreground">{data.prompt}</p>
          <ExpandTrigger />
        </div>
      )}
      <div className="relative">
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="overflow-y-auto pr-1"
          style={{ maxHeight: maxHeight ?? undefined }}
        >
          {list}
        </div>
        {hiddenCount > 0 && !atBottom && (
          <>
            <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-background via-background/90 to-transparent" />
            <button
              type="button"
              onClick={() => {
                const el = scrollRef.current;
                if (!el) return;
                el.scrollBy({ top: el.clientHeight, behavior: 'smooth' });
              }}
              className="absolute left-1/2 bottom-1 -translate-x-1/2 flex items-center gap-1 rounded-full border bg-card px-2 py-0.5 text-[10px] font-medium text-muted-foreground shadow-sm hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={`Scroll to see ${hiddenCount} more option${hiddenCount === 1 ? '' : 's'}`}
            >
              +{hiddenCount} more
              <RiArrowDownSLine className="h-3 w-3" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function Question({
  data,
  toolCallId,
  preSelectedId,
}: {
  data: QuestionBlock;
  toolCallId?: string;
  preSelectedId?: string | null;
}) {
  const sendMessage = useChatSend();
  const [selectedId, setSelectedId] = useState<string | null>(preSelectedId ?? null);
  const hasSelection = selectedId !== null;

  const handleSelect = (option: QuestionOption) => {
    if (selectedId !== null) return;
    setSelectedId(option.id);
    const text = buildSelectionMessage(option);
    sendMessage?.({
      text,
      parts: [
        { type: 'text', text },
        ...(toolCallId
          ? [
              {
                type: 'data-user-answer' as const,
                data: {
                  toolCallId,
                  answerId: option.id,
                  answerLabel: option.label,
                },
              },
            ]
          : []),
      ],
    });
  };

  return (
    <ExpandableBlock
      title={data.prompt}
      expandedContent={
        hasSelection ? undefined : (
          <QuestionContent
            data={data}
            onSelect={handleSelect}
            selectedId={selectedId}
            inline={false}
          />
        )
      }
    >
      <QuestionContent data={data} onSelect={handleSelect} selectedId={selectedId} inline={true} />
    </ExpandableBlock>
  );
}

export function QuestionSkeleton() {
  return (
    <div className="py-1">
      <Skeleton className="h-3 w-[60%]" />
      <div className="mt-3 space-y-2">
        {['a', 'b', 'c'].map((id) => (
          <div key={id} className="rounded-md border px-3 py-2">
            <Skeleton className="h-3 w-[55%]" />
            <Skeleton className="mt-1 h-2.5 w-[75%]" />
          </div>
        ))}
      </div>
    </div>
  );
}
